const Math2D = require("../../util/Math2D");
const _ = require("lodash");
const RenderHelpers = require("./RenderHelpers");

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const RenderController = function(context, data) {
	this.context = context;
	this.data = data || {};

	this.measureNode = this.data.measureNode;
	this.drawNode = this.data.drawNode;
	this.isSpaceNode = this.data.isSpaceNode;
	this.isBlockNode = this.data.isBlockNode;

	this.initTransform = context.getTransform();

	this._lineYStack = [];
	this._measureCache = new Map();
	this._erroredNodes = new Set();

	this.isSkippingCache = false;
	this.debugBoxes = false;
	this.errors = [];
	this.blanks = [];
	this.parentRc = null;
	if (context.__diagramRc) {
		this.parentRc = context.__diagramRc;
	}
};

RenderController.prototype.clone = function() {
	const rc = new RenderController(this.context, {
		measureNode: this.measureNode,
		drawNode: this.drawNode,
		isSpaceNode: this.isSpaceNode,
		isBlockNode: this.isBlockNode,
	});

	// TODO: These are used during measure only, which means they are okay to
	// use directly in the clone. Cloning is only done between measuring and
	// drawing atm, but fix this if we ever use clone somewhere else.
	rc._measureCache = this._measureCache;
	rc._erroredNodes = this._erroredNodes;

	rc.errors = _.clone(this.errors);
	rc.blanks = _.clone(this.blanks);
	return rc;
};

RenderController.prototype.getMeasure = function(node) {
	return this._measureCache.get(node);
};

RenderController.prototype.setMeasure = function(node, measure) {
	this._measureCache.set(node, measure);
};

// Hack to obtain a tricky feature in blanks. Call pushLineY when a wrapping
// node starts drawing a line, and popLineY after. In Banvas, exp blanks use
// the line Y to determine what to fake their rectangle as.

RenderController.prototype.pushLineY = function(y, top, bottom, fontInfo) {
	this._lineYStack.push({y, top, bottom, fontInfo});
};

RenderController.prototype.popLineY = function() {
	return this._lineYStack.pop();
};

RenderController.prototype.getLineY = function() {
	return this._lineYStack[this._lineYStack.length - 1] || null;
};

RenderController.prototype.isErrored = function(node) {
	return !!this._erroredNodes.has(node);
};

RenderController.prototype.setErrored = function(node) {
	this._erroredNodes.add(node);
};

RenderController.prototype.pushError = function(message) {
	this.errors.push({
		message: message,
		whileRendering: true,
	});

	if (this.parentRc) {
		this.parentRc.pushError(message);
	}
};

RenderController.prototype.mergeErrors = function(errors) {
	this.errors = this.errors.concat(errors);

	if (this.parentRc) {
		this.parentRc.mergeErrors(errors);
	}
};

// Run the provided function, which is passed this rc as an argument. When it
// finishes running, return an array of rc errors pushed during the function,
// suitable to call with mergeErrors in other rc instances.
RenderController.prototype.trackErrors = function(f) {
	const origLength = this.errors.length;
	f(this);
	return this.errors.slice(origLength);
};

// If called, CachedCanvasMath will not store the results in the cache.
RenderController.prototype.skipCacheThisRun = function() {
	this.isSkippingCache = true;
};

RenderController.prototype.transformRect = function(rect) {
	const startingTransform = this.initTransform;
	const invStartingTransform = Math2D.invertTransform(startingTransform);
	const currentTransform = this.context.getTransform();
	rect = Math2D.transformRect(currentTransform, rect);
	rect = Math2D.transformRect(invStartingTransform, rect);
	return rect;
};

RenderController.prototype.pushBlank = function(obj) {
	if (!obj) {
		return;
	}

	if (this.parentRc) {
		this.parentRc.pushBlank(_.cloneDeep(obj));
	}

	const transformRect = rect => {
		rect = this.transformRect(rect);
		return {
			x: rect[0],
			y: rect[1],
			width: rect[2],
			height: rect[3],
		};
	};

	const lineY = this.getLineY();
	if (lineY) {
		const natural = RenderHelpers.getNaturalBlankMeasure(this, lineY.fontInfo);
		obj.naturalLineBlank = transformRect([
			obj.x,
			lineY.y - natural.top,
			obj.width,
			natural.top + natural.bottom,
		]);
	}

	_.extend(obj, transformRect([obj.x, obj.y, obj.width, obj.height]));
	const num = obj.typeInfo && obj.typeInfo.numerator;
	if (num) {
		_.extend(num, transformRect([num.x, num.y, num.width, num.height]));
	}

	const denom = obj.typeInfo && obj.typeInfo.denominator;
	if (denom) {
		_.extend(
			denom,
			transformRect([denom.x, denom.y, denom.width, denom.height])
		);
	}

	const whole = obj.typeInfo && obj.typeInfo.whole;
	if (whole) {
		_.extend(
			whole,
			transformRect([whole.x, whole.y, whole.width, whole.height])
		);
	}

	this.blanks.push(obj);
};

module.exports = RenderController;
